import { useState, useEffect } from "react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, Globe, Clock, ArrowRight, CheckCircle2 } from "lucide-react";
import { pillars } from "@/data/pillars";
import LandingNav from "@/components/landing/LandingNav";
import LandingFooter from "@/components/landing/LandingFooter";
import LandingHero from "@/components/landing/LandingHero";
import LandingFAQ from "@/components/landing/LandingFAQ";
import EmailGateModal from "@/components/EmailGateModal";
import WaitlistModal from "@/components/WaitlistModal";
import WaitlistBanner from "@/components/WaitlistBanner";

const faqs = [
  {
    q: "Is this SEO audit really free?",
    a: "Yes — 100% free, no credit card or signup required. Enter your URL and get a full SEO audit report covering on-page SEO, technical health, AI visibility, backlinks, competitors, ranking potential, and a prioritized action plan.",
  },
  {
    q: "What does a free SEO audit check?",
    a: "Our free SEO audit analyzes over 50 ranking factors across 7 modules: SEO foundation (meta tags, headings, content quality), keyword analysis, AI visibility (ChatGPT, Gemini, AI Overviews), backlink profile, competitor benchmarking, ranking prediction, and generates a prioritized action plan.",
  },
  {
    q: "How long does the SEO audit take?",
    a: "Most audits complete in under 2 minutes. Our AI engine crawls your site, evaluates technical SEO, content quality, backlinks, and AI visibility in real time — then generates a full report with actionable recommendations.",
  },
  {
    q: "Do I need to install anything to run the audit?",
    a: "No installation needed. Unranked is a cloud-based SEO audit tool — just paste your URL into the analyzer and get your results. Works on any publicly accessible website.",
  },
  {
    q: "How is Unranked different from other free SEO audit tools?",
    a: "Most free SEO tools only check basic on-page elements. Unranked goes further with AI visibility analysis (how your site appears in ChatGPT, Gemini, AI Overviews), competitor intelligence, backlink evaluation, ranking prediction, and a prioritized action plan — all in one free report.",
  },
  {
    q: "Can I audit any website?",
    a: "Yes — Unranked works with any publicly accessible website, from small business sites and blogs to large e-commerce stores and SaaS platforms. If search engines can crawl it, we can audit it.",
  },
  {
    q: "What is AI visibility and why does it matter for SEO?",
    a: "AI visibility measures how likely your content is to be cited by AI-powered search tools like ChatGPT, Google AI Overviews, and Gemini. As more users discover information through AI, optimizing for AI visibility is becoming as important as traditional SEO.",
  },
];

const trust = [
  { icon: Sparkles, text: "AI-powered analysis" },
  { icon: Globe, text: "1 free audit — no signup needed" },
  { icon: Clock, text: "Full report in under 2 minutes" },
];

const FreeAuditToolPage = () => {
  const [gateUrl, setGateUrl] = useState("");
  const [showGate, setShowGate] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);

  usePageMeta({
    title: "Free SEO Audit Tool — Analyze Your Website in Seconds | Unranked",
    description:
      "Run a free, comprehensive SEO audit on any website. Get AI visibility analysis, backlink intelligence, competitor benchmarks, and a prioritized action plan — no signup required.",
    canonical: "https://unrankedagency.com/tools/free-seo-audit",
  });

  // JSON-LD: SoftwareApplication + FAQPage
  useEffect(() => {
    const jsonLd = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "SoftwareApplication",
          "@id": "https://unrankedagency.com/tools/free-seo-audit#app",
          name: "Unranked Free SEO Audit Tool",
          applicationCategory: "SEO Tool",
          operatingSystem: "Web",
          description:
            "Free AI-powered SEO audit tool that analyzes your website across 7 modules — technical SEO, keywords, AI visibility, backlinks, competitors, ranking prediction, and action plan.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          url: "https://unrankedagency.com/tools/free-seo-audit",
          creator: { "@id": "https://unrankedagency.com/#organization" },
        },
        {
          "@type": "FAQPage",
          "@id": "https://unrankedagency.com/tools/free-seo-audit#faq",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleAnalyze = (url: string) => {
    setGateUrl(url);
    setShowGate(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <LandingNav onJoinWaitlist={() => setShowWaitlist(true)} />

      <main>
        <LandingHero
          badge="Free SEO Audit Tool"
          title={
            <>
              Free SEO Audit Tool —{" "}
              <span className="gradient-text">Analyze Your Website</span> in
              Seconds
            </>
          }
          subtitle="Get a comprehensive SEO audit with AI visibility analysis, backlink intelligence, competitor benchmarks, and a prioritized action plan. Paste your URL — no signup required."
          showUrlInput
          onAnalyze={handleAnalyze}
          ctaLabel="Run Free SEO Audit"
          trustItems={trust}
        />

        {/* What You Get */}
        <section className="container mx-auto px-6 py-20" aria-labelledby="what-you-get">
          <div className="text-center mb-12">
            <h2 id="what-you-get" className="text-3xl md:text-4xl font-bold mb-3">
              What Does the Free SEO Audit Include?
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              7 in-depth analysis modules covering every angle of your search visibility.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {pillars.map((p) => (
              <article
                key={p.num}
                className="group p-6 rounded-xl bg-card border border-border hover:border-primary/30 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <p.icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">{p.num}</span>
                </div>
                <h3 className="font-semibold text-foreground mb-1">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              </article>
            ))}
          </div>
        </section>

        {/* How It Works */}
        <section className="container mx-auto px-6 py-20" aria-labelledby="how-heading">
          <div className="text-center mb-14">
            <h2 id="how-heading" className="text-3xl md:text-4xl font-bold mb-3">
              How the Free SEO Audit Works
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Three steps to a full-spectrum SEO report.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                step: 1,
                title: "Paste Your URL",
                desc: "Enter any website address — no account, no installation, no credit card.",
              },
              {
                step: 2,
                title: "AI Analyzes Your Site",
                desc: "Our engine crawls your pages and evaluates 50+ ranking factors across 7 modules in real time.",
              },
              {
                step: 3,
                title: "Get Your Full Report",
                desc: "Receive a detailed audit with scores, benchmarks, and a prioritized action plan you can act on immediately.",
              },
            ].map((s) => (
              <div key={s.step} className="text-center space-y-4">
                <div className="mx-auto w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold">
                  {s.step}
                </div>
                <h3 className="font-semibold text-foreground text-lg">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why Use Our SEO Audit Tool */}
        <section className="container mx-auto px-6 py-20" aria-labelledby="why-heading">
          <div className="text-center mb-14">
            <h2 id="why-heading" className="text-3xl md:text-4xl font-bold mb-3">
              Why Use Our Free SEO Audit Tool?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              "Covers AI visibility — not just traditional SEO",
              "Analyzes backlinks, competitors, and ranking potential",
              "Generates a prioritized action plan sorted by impact",
              "No signup, no credit card, no installation required",
              "Full report delivered in under 2 minutes",
              "Works on any publicly accessible website",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <p className="text-foreground">{item}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              size="lg"
              className="h-14 px-10 rounded-xl text-base font-semibold glow-primary"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              Run Your Free SEO Audit Now
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </section>

        {/* Related Guides */}
        <section className="container mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold mb-3">Related Resources</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
            <Button variant="outline" asChild>
              <Link to="/">← Back to Homepage</Link>
            </Button>
          </div>
        </section>

        <LandingFAQ
          title="Free SEO Audit Tool — FAQ"
          subtitle="Common questions about our free website SEO audit."
          faqs={faqs}
        />

        <WaitlistBanner variant="full" onJoinClick={() => setShowWaitlist(true)} />
      </main>

      <LandingFooter />

      <EmailGateModal open={showGate} onOpenChange={setShowGate} url={gateUrl} />
      <WaitlistModal open={showWaitlist} onOpenChange={setShowWaitlist} />
    </div>
  );
};

export default FreeAuditToolPage;
